/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import {Observable, of} from 'rxjs';
import {map, take, takeUntil} from 'rxjs/operators';

import {AbstractField} from './abstract-field';

import {EmpZoneDependencyUtils} from '../emp-zone-dependency.utils';
import {EmpZoneDependency} from '../form-generator.interfaces';

/**
 * A static list type of fields : all possible values are known in advance.
 */
@Component({
  selector: 'gp-form-field-list',
  template: `
    <ng-select class="form-control"
               [(ngModel)]="value.value"
               [attr.test-id]="descriptor.zoneID"
               (ngModelChange)="onValueChange($event)"
               [formControl]="control"
               [idField]="'value'"
               [textField]="'display'"
               (focus)="onFocus()"
               (blur)="onBlur()"
               [readOnly]="readOnly"
               [items]="items$ | async"
               [sortByTranslationsEnabled]=false
               [parentScrollSelector]="parentScrollSelector">
    </ng-select>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListFieldComponent extends AbstractField implements OnInit {

  public parentScrollSelector: string;
  public items$: Observable<Array<any>>;

  private whitelist: string[] = null;

  constructor(protected translateService: TranslateService,
              protected cd: ChangeDetectorRef) {
    super();
  }

  private static itemDisplayOrder(item: any): number {
    return (item.displayOrder || Number.MAX_SAFE_INTEGER);
  }

  private static itemDisplay(item: any, translations: any): string {
    return (translations[item.display] || item.display);
  }

  public ngOnInit() {
    this.items$ = this.items();
    this.bindDependencyTriggered();
    this.autoSetIfNecessary(this.descriptor?.metadatas?.items || []);

    this.translateService.onLangChange
      .asObservable()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.items$ = this.items();
      });
  }

  protected handleFilter(dependency: EmpZoneDependency) {
    if (EmpZoneDependencyUtils.isReset(dependency)) {
      this.whitelist = null;
    } else {
      this.whitelist = dependency.actionValue.split(',');
      if (!this.whitelist.includes(this.value?.value)) {
        this.value.value = '';
        this.value.transId = null;
      }
    }
  }

  protected afterDependencyHandled(dependency: EmpZoneDependency) {
    this.items$ = this.items();
    this.cd.markForCheck();
  }

  public onValueChange(value: any) {
    if (value) {
      const listItem: any = this.descriptor.metadatas.items.find((item: any) => item.value === value);
      if (listItem) {
        this.value.value = listItem.value;
        this.value.transId = listItem.display;
      }
    } else {
      this.value.value = '';
      this.value.transId = null;
    }
  }

  public items(): Observable<Array<any>> {
    const items = this.descriptor?.metadatas?.items
      .filter(item => !this.whitelist || this.whitelist.includes(item.value));
    if (items?.length > 0) {
      return this.translateService.get(items.map(item => item.display))
        .pipe(
          take(1),
          map((translations: any) => {
            return items.sort((itema, itemb) => {
              const compare = ListFieldComponent.itemDisplayOrder(itema) - ListFieldComponent.itemDisplayOrder(itemb);
              if (compare === 0) {
                return ListFieldComponent.itemDisplay(itema, translations)
                  .localeCompare(ListFieldComponent.itemDisplay(itemb, translations));
              }
              return compare;
            });
          }));
    }
    return of([]);
  }

  private autoSetIfNecessary(items: any[]) {
    if (items.length === 1 && !this.descriptor.readOnly && this.descriptor.isRequired && !this.value.cleared) {
      const singleItem = items[0];

      if (!this.value.value || this.value.value !== singleItem.value) {
        this.value.value = singleItem.value;
        this.value.transId = singleItem.display;

        if ('cleared' in this.value) {
          delete this.value.cleared;
        }

        this.control.setValue(singleItem.value);
        this.control.markAsDirty();
        this.control.markAsTouched();
        this.control.updateValueAndValidity();

        if (this.control.parent) {
          this.control.parent.markAsDirty();
          this.control.parent.updateValueAndValidity();
        }
      }
    }
  }
}
